import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { useSession } from '@context/sessionProvider';
import { sendDataAPI } from '@lib/sendDataAPI';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

declare global {
	interface Window {
		amplitudeInitialized?: boolean;
	}
}

interface AmplitudeContextValue {
	amplitudeInstance: typeof amplitude | null;
}

const AmplitudeContext = createContext<AmplitudeContextValue>({
	amplitudeInstance: null,
});

export const useAmplitude = () => useContext(AmplitudeContext);

interface AmplitudeProviderProps {
	children: ReactNode;
	onLoad?: () => void;
	onError?: (error: string) => void;
}

export const AmplitudeProvider: React.FC<AmplitudeProviderProps> = ({ children, onLoad, onError }) => {
	const [amplitudeInstance, setAmplitudeInstance] = useState<typeof amplitude | null>(null);
	const { context } = useSession();

	useEffect(() => {
		const initializeAmplitude = async () => {
			try {
				if (typeof window !== 'undefined' && !window.amplitudeInitialized) {
					const response = await sendDataAPI({ jwtToken: context }, '/api/amplitude/get', 'GET');

					if (!response.ok) {
						throw new Error('Failed to fetch Amplitude configuration.');
					}

					const responseBody = await response.json();

					const { is_enabled, key, instance_id } = responseBody.data;

					if (is_enabled) {
						window.amplitudeInitialized = true;

						amplitude.init(key, instance_id, {});
						const sessionReplayTracking = sessionReplayPlugin();
						await amplitude.add(sessionReplayTracking).promise;

						setAmplitudeInstance(amplitude);
					}

					if (onLoad) {
						onLoad();
					}
				}
			} catch (error) {
				console.error('Failed to initialize Amplitude', error);
				if (onError) {
					onError('Failed to initialize Amplitude. Please try again.');
				}
			}
		};

		initializeAmplitude();
	}, [context, onLoad, onError]);

	return <AmplitudeContext.Provider value={{ amplitudeInstance }}>{children}</AmplitudeContext.Provider>;
};
